// eslint-disable-next-line no-restricted-imports
import { ListedInstrument as IListedInstrument } from '@eih-netbroker/api-instrument';
import { Keys } from '../../config/keys';
import { EStreamAllDataDTO } from '../estream/eStreamTrade';
import { Inventory } from '../portfolio/inventory';
import { Instrument } from './instrument';

export type ListedInstrument = IListedInstrument;

export type ListedInstrumentWithId = ListedInstrument & { id: string };

export type ListedInstrumentLike = {
  ric: string;
  mic: string;
  instrumentCategory?: InstrumentCategoryEnum;
  lastClosePrice?: number;
  currency?: string;
};

export enum InstrumentCategoryEnum {
  Equity = 'EQ',
  Futures = 'FUT',
  Certificate = 'CRT',
  Warrant = 'WAR',
  ETF = 'ETF',
  InvestmentFund = 'FND',
  GovernmentBond = 'GB',
  Bond = 'BND',
  CompensationNote = 'CN',
  Option = 'OPT',
  Currency = 'CCY',
  StructuredProduct = 'SRP',
  Index = 'IND',
  Other = 'OTH',
}

/**
 * Function to generate deterministic unique ID for an instrument like or EStreamAllDataDTO.
 * Returns undefined when the item is not a ListedInstrumentLike or EStreamAllDataDTO.
 */
export const getInstrumentId = (
  item: ListedInstrument | ListedInstrumentLike | EStreamAllDataDTO | Inventory,
): string | undefined => {
  if ((item as ListedInstrumentLike).ric) {
    return (item as ListedInstrumentLike).ric;
  }
  console.error('Missing RIC', item);
  return undefined;
};

/**
 * Function to generate deterministic unique ID for an instrument like or EStreamAllDataDTO.
 * Throws error when the item is not a ListedInstrumentLike or EStreamAllDataDTO.
 * @param item
 */
export const getInstrumentIdForce = (
  item: ListedInstrument | ListedInstrumentLike | EStreamAllDataDTO | Inventory,
): string | never => {
  const id = getInstrumentId(item);
  if (!id) {
    throw new Error(`Instrument ID cannot be undefined!`);
  }
  return id;
};

export const getUnknownListedInstrument = (ric: string, name: string): ListedInstrument => {
  return {
    ticker: `N/A (${ric})`,
    ric: ric,
    currency: 'XXX',
    tradableInApp: false,
    mainMarketMICCode: Keys.Markets.Mic.Unknown,
    mainMarketRIC: `N/A (${ric})`,
    allowToNYESZ: false,
    tradeable: false,
    instrumentName: `${name} ${ric}`,
    isin: Keys.Instruments.Isin.Unknown,
    instrumentCategory: InstrumentCategoryEnum.Equity,
    canBeShorted: false,
    collateralValue: 0,
    mic: Keys.Markets.Mic.Unknown,
    marketName: 'N/A',
    marketShortName: 'N/A',
    defaultMarket: true,
    bestExecution: true,
  };
};

export const isUnknownInstrument = (instrument: ListedInstrument) => {
  if (instrument.isin === Keys.Instruments.Isin.Unknown && instrument.mainMarketMICCode === Keys.Markets.Mic.Unknown) {
    return true;
  }

  return false;
};

export const createInstrumentorListedInstrumentFromInventory = (
  inventory: Inventory,
  instrument: ListedInstrument | null,
) => {
  if (!inventory.ric && !inventory.isin) {
    throw new Error(`Inventory ric and isin cannot be empty at same time!`);
  }

  const listedInstrumentAndBase: {
    instrumentBase: Instrument;
    instrument?: ListedInstrument;
  } = {
    instrumentBase: {
      instrumentName: inventory.instrumentName,
      isin: inventory.isin!,
      instrumentCategory: inventory.instrumentCategory,
      canBeShorted: true,
    },
  };

  if (inventory.ric) {
    listedInstrumentAndBase.instrument = instrument || {
      // TODO: inventory must contain
      allowToNYESZ: true,
      // TODO: inventory must contain
      canBeShorted: true,
      // TODO: inventory must contain
      collateralValue: 0,
      // TODO: inventory must contain
      defaultMarket: true,
      // TODO: inventory must contain
      bestExecution: true,
      instrumentName: inventory.instrumentName,
      ticker: inventory.ticker || '',
      mic: inventory.marketMICCode || '-',
      ric: inventory.ric,
      currency: inventory.initCcy,
      isin: inventory.isin || Keys.Instruments.Isin.Unknown,
      instrumentCategory: inventory.instrumentCategory || InstrumentCategoryEnum.Equity,
      marketName: inventory.marketName || 'N/A',
      marketShortName: inventory.marketName || 'N/A',
      reutersRegionCode: '',
      tradeable: false,
      tradableInApp: false,
    };
  }

  return listedInstrumentAndBase;
};

export const isListedInstrumentTradeable = (instrument?: ListedInstrument | null) => {
  return !!(instrument && instrument.tradeable);
};

export const isListedInstrumentTradableInApp = (instrument?: ListedInstrument | null) => {
  return !!(instrument && instrument.tradableInApp);
};

/**
 * Returns true if we can calculate real-time PnL for the instrument category (RIC code is also required)
 * @param instrumentCategory
 */
export const isRealTimeInstrumentCategory = (instrumentCategory: InstrumentCategoryEnum) => {
  return (
    instrumentCategory === InstrumentCategoryEnum.Equity ||
    instrumentCategory === InstrumentCategoryEnum.Certificate ||
    instrumentCategory === InstrumentCategoryEnum.ETF ||
    instrumentCategory === InstrumentCategoryEnum.CompensationNote
  );
};

export const isListedInstrumentHasRealTimePnl = (instrument?: ListedInstrument | null) => {
  if (
    instrument &&
    instrument.instrumentCategory &&
    (instrument.instrumentCategory === InstrumentCategoryEnum.Equity ||
      instrument.instrumentCategory === InstrumentCategoryEnum.Certificate ||
      instrument.instrumentCategory === InstrumentCategoryEnum.ETF ||
      instrument.instrumentCategory === InstrumentCategoryEnum.CompensationNote)
  ) {
    return true;
  }
  return false;
};
